import { useState, useEffect, useCallback, useRef } from 'react';
import { Grid, Typography, TextField, Select, MenuItem, Divider } from '@mui/material';
import { useNavigate } from 'react-router';
import intl from 'components/i18n/ReactIntlWrapper';
import NewInvoiceItemTable from '../NewInvoice/NewInvoiceItemTable';
import NewInvoiceSummary from '../NewInvoice/NewInvoiceSummary';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useInvoice } from '../common/InvoiceProvider';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import WorkflowActionsBar from '../Workflow/WorkflowActionsBar';
import InvoiceReconciliationDialog from './InvoiceReconciliationDialog';
import { handleAPIError } from '../../common/errorHandler';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Spinner from 'components/Spinner';
import { useWorkflow } from 'components/Workflow/WorkflowContext';
import { WorkflowStatusChip } from 'components/common/WorkflowStatusChip';

const InvoicingForm = ({ disabled = false }) => {
  const {
    currencies,
    invoiceTypes,
    billToPersonas,
    input,
    setInput,
    errors,
    setErrors,
    isLoading: isInvoiceLoading,
  } = useInvoice();
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToastMessageQueue();
  const api: Api = useApi();
  const navigate = useNavigate();
  const { workflow, loading: workflowLoading } = useWorkflow();
  const [workflowState, setWorkflowState] = useState<any>(null);

  // Reconciliation dialog state
  const [reconciliationOpen, setReconciliationOpen] = useState(false);
  const pendingReconcileResolve = useRef<((result: boolean | 'handled') => void) | null>(null);
  const pendingReconcileStateId = useRef<number | null>(null);

  const handleBeforeAction = useCallback(
    (targetStateId: number, actionName?: string): Promise<boolean | 'handled'> => {
      if (actionName === 'Reconciliate') {
        pendingReconcileStateId.current = targetStateId;
        setReconciliationOpen(true);
        return new Promise<boolean | 'handled'>((resolve) => {
          pendingReconcileResolve.current = resolve;
        });
      }
      return Promise.resolve(true);
    },
    []
  );

  const handleReconciliationConfirm = async () => {
    setReconciliationOpen(false);
    const targetStateId = pendingReconcileStateId.current;
    pendingReconcileResolve.current?.('handled');
    pendingReconcileResolve.current = null;
    pendingReconcileStateId.current = null;

    if (targetStateId != null && input.id) {
      try {
        await api.workflowApi.apiVversionWorkflowUpdateStatePost('1', {
          workflowStateId: targetStateId,
          entityId: input.id,
          moduleId: workflow?.idModule ?? 3,
          comments: null,
        });
        navigate('/invoicing');
      } catch (error) {
        handleAPIError(error, toast, errors);
      }
    }
  };

  const handleReconciliationCancel = () => {
    setReconciliationOpen(false);
    pendingReconcileResolve.current?.(false);
    pendingReconcileResolve.current = null;
    pendingReconcileStateId.current = null;
  };

  useEffect(() => {
    if (workflow && !workflowLoading) {
      setWorkflowState(workflow.states.find((state) => state.id === input.idWorkflowState));
    }
  }, [workflow, workflowLoading, input.idWorkflowState]);

  useEffect(() => {
    const handleAutofill = () => {
      const inputs = document.querySelectorAll('input, select');
      inputs.forEach((input) => {
        const element = input as HTMLInputElement | HTMLSelectElement;
        if (element.value && !element.dataset.initialized) {
          element.dataset.initialized = 'true';
          const event = new Event('change', { bubbles: true });
          element.dispatchEvent(event);
        }
      });
    };

    handleAutofill();
    const timeoutId = setTimeout(handleAutofill, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleInputChange = (name, value) => {
    if (name === 'idTo') {
      setInput({
        ...input,
        [name]: value,
        to: billToPersonas.find((persona) => persona.id === value).name,
      });
    } else {
      setInput({
        ...input,
        [name]: value,
      });
    }
    //validate errors
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const handleSave = async (id, comments) => {
    setLoading(true);
    let result = false;
    try {
      input.comments = comments;
      const errorList = {};
      if (!input.number) errorList['number'] = `${intl.get('newInvoice.numberRequired')}`;
      if (!input.idCurrency || input.idCurrency === 0 || input.idCurrency === '')
        errorList['idCurrency'] = `${intl.get('newInvoice.currencyRequired')}`;
      if (!input.idInvoiceType || input.idInvoiceType === 0 || input.idInvoiceType === '')
        errorList['idInvoiceType'] = `${intl.get('newInvoice.isRequired')}`;
      if (!input.date) errorList['date'] = `${intl.get('newInvoice.dateRequired')}`;
      if (!input.addressFrom) errorList['addressFrom'] = `${intl.get('newInvoice.isRequired')}`;
      if (!input.addressTo) errorList['addressTo'] = `${intl.get('newInvoice.isRequired')}`;
      if (!input.idFrom || input.idFrom === 0 || input.idFrom === '')
        errorList['idFrom'] = `${intl.get('newInvoice.profileRequired')}`;
      if (!input.idTo || input.idTo === 0 || input.idTo === '')
        errorList['idTo'] = `${intl.get('newInvoice.isRequired')}`;
      if (!input.invoiceItems || input.invoiceItems.length === 0)
        errorList['invoiceItems'] = `${intl.get('newInvoice.itemsRequired')}`;
      input.invoiceItems.forEach((item) => {
        if (
          item.description === '' ||
          !item.description ||
          item.quantity === '' ||
          !item.quantity ||
          item.rate === '' ||
          !item.rate
        ) {
          errorList['invoiceItems'] = 'Missing one or more field in items';
        }
      });
      setErrors((prevErrors) => ({ ...prevErrors, ...errorList }));

      const filteredErrors = Object.fromEntries(
        Object.entries(errors).filter(
          ([key, value]) => value !== 'missing confidence' && value !== ''
        )
      );
      if (Object.keys(filteredErrors).length > 0) {
        setLoading(false);
        return;
      }

      const inputCopy = { ...input };

      if (inputCopy.invoiceItems) {
        inputCopy.invoiceItems = inputCopy.invoiceItems.map((item) => {
          const newItem = { ...item };
          newItem.amount = item.rate;
          newItem.idInvoice = inputCopy.id;
          newItem.id = item.id < 0 ? 0 : item.id;

          if (newItem.invoiceItemTaxes && newItem.invoiceItemTaxes.length > 0) {
            const itemAmount = (Number(newItem.quantity) || 0) * (Number(newItem.amount) || 0);
            newItem.invoiceItemTaxes = newItem.invoiceItemTaxes.map((tax) => ({
              idTaxRate: tax.id,
              taxAmount:
                tax.amount === 0 ? itemAmount * (Number(tax.rate) / 100) : Number(tax.amount),
              taxRate: Number(tax.rate) || 0,
              idInvoiceItem: newItem.id,
            }));
          }
          return newItem;
        });
      }

      if (inputCopy.invoiceTaxes) {
        inputCopy.invoiceTaxes = inputCopy.invoiceTaxes.map((tax) => ({
          id: tax.id || 0,
          idInvoice: inputCopy.id,
          idTaxRate: tax.idTaxRate,
          taxAmount: tax.taxAmount,
          taxRate: tax.taxRate,
          description: tax.description,
        }));
      }

      inputCopy.idWorkflowState = id;
      inputCopy.number = inputCopy.number.toString();
      inputCopy.idInvoiceType = Number(inputCopy.idInvoiceType);

      if (inputCopy.attachments && !inputCopy.fileURI) {
        inputCopy.fileURI = inputCopy.attachments[0].fileURI;
      }

      if (inputCopy.invoice) {
        const reader = new FileReader();
        reader.readAsDataURL(inputCopy.invoice);
        await new Promise((resolve, reject) => {
          reader.onload = () => {
            inputCopy.fileURI = reader.result;
            delete inputCopy.invoice;
            resolve(void 0);
          };
          reader.onerror = () => {
            reject(new Error('Failed to read file'));
          };
        });
      }

      const formatedInput = {
        ...inputCopy,
        date: new Date(inputCopy.date).toISOString().split('T')[0],
      };
      const response = await api.invoiceApi.apiVversionInvoicePut('1', formatedInput);
      if (response.data?.data) {
        toast.success({ message: intl.get('newInvoice.toast.success') });
        navigate('/invoicing');
        result = true;
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      setLoading(false);
      result = false;
    }
    setLoading(false);
    return result;
  };

  const isDisabled = () => {
    return (
      !input.idCurrency ||
      !input.idInvoiceType ||
      !input.date ||
      !input.idFrom ||
      !input.idTo ||
      !input.invoiceItems ||
      input.invoiceItems?.some(
        (item) =>
          !item.description ||
          item.description === '' ||
          !item.quantity ||
          item.quantity === '' ||
          !item.amount ||
          item.amount === ''
      ) ||
      input.invoiceTaxes?.some(
        (tax) => errors?.[`invoiceTaxes[${tax.idTaxRate}].description`] === 'missing confidence'
      ) ||
      input.invoiceItems?.some((item) =>
        item.invoiceItemTaxes?.some((tax) => tax.id === 0 || tax.id === null)
      )
    );
  };

  const performSaveUpdate = async (): Promise<boolean> => {
    const inputCopy = { ...input };

    if (inputCopy.invoiceItems) {
      inputCopy.invoiceItems = inputCopy.invoiceItems.map((item) => {
        const newItem = { ...item };
        newItem.amount = item.rate;
        newItem.idInvoice = inputCopy.id;
        newItem.id = item.id < 0 ? 0 : item.id;

        if (newItem.invoiceItemTaxes && newItem.invoiceItemTaxes.length > 0) {
          const itemAmount = (Number(newItem.quantity) || 0) * (Number(newItem.amount) || 0);
          newItem.invoiceItemTaxes = newItem.invoiceItemTaxes.map((tax) => ({
            idTaxRate: tax.id,
            taxAmount:
              tax.amount === 0 ? itemAmount * (Number(tax.rate) / 100) : Number(tax.amount),
            taxRate: Number(tax.rate) || 0,
            idInvoiceItem: newItem.id,
          }));
        }
        return newItem;
      });
    }

    if (inputCopy.invoiceTaxes) {
      inputCopy.invoiceTaxes = inputCopy.invoiceTaxes.map((tax) => ({
        id: tax.id || 0,
        idInvoice: inputCopy.id,
        idTaxRate: tax.idTaxRate,
        taxAmount: tax.taxAmount,
        taxRate: tax.taxRate,
        description: tax.description,
      }));
    }

    inputCopy.number = inputCopy.number?.toString();
    inputCopy.idInvoiceType = Number(inputCopy.idInvoiceType);

    if (inputCopy.attachments && !inputCopy.fileURI) {
      inputCopy.fileURI = inputCopy.attachments[0].fileURI;
    }

    if (inputCopy.invoice) {
      const reader = new FileReader();
      reader.readAsDataURL(inputCopy.invoice);
      await new Promise((resolve, reject) => {
        reader.onload = () => {
          inputCopy.fileURI = reader.result;
          delete inputCopy.invoice;
          resolve(void 0);
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
      });
    }

    const formatedInput = {
      ...inputCopy,
      date: new Date(inputCopy.date).toISOString().split('T')[0],
    };

    const response = await api.invoiceApi.apiVversionInvoicePut('1', formatedInput);
    return !!response.data?.data;
  };

  const handleSaveOnly = async () => {
    setLoading(true);
    try {
      const success = await performSaveUpdate();
      if (success) {
        toast.success({ message: intl.get('newInvoice.toast.success') });
        navigate('/invoicing');
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    }
    setLoading(false);
  };

  const handleSaveBeforeAction = async () => {
    setLoading(true);
    try {
      const success = await performSaveUpdate();
      if (success) {
        toast.success({ message: intl.get('newInvoice.toast.success') });
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      setLoading(false);
      throw error;
    }
    setLoading(false);
  };

  return (
    <>
      <Grid container spacing={2}>
      {(isInvoiceLoading || loading) && <Spinner />}
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >

            <Typography gutterBottom>{intl.get('invoicesList.invoice.number')}</Typography>
            {errors?.number && errors?.number !== 'missing confidence' && (
              <Typography className="text-danger">{errors?.number}</Typography>
            )}
            <TextField
              fullWidth
              size="small"
              id="number"
              name="number"
              placeholder={intl.get('newInvoice.invoiceNumber')}
              value={input?.number}
              onChange={(e) => handleInputChange('number', e.target.value)}
              onBlur={(e) => handleInputChange('number', e.target.value)}
              disabled={disabled}
              error={!!errors?.number}
            />
          </Grid>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom>{intl.get('newInvoice.invoiceCurrency')}</Typography>
            {errors?.idCurrency && (
              <Typography className="text-danger">{errors?.idCurrency}</Typography>
            )}
            <Select
              fullWidth
              size="small"
              id="currency"
              name="currency"
              value={input?.idCurrency}
              displayEmpty
              disabled={disabled}
              inputProps={{ placeholder: intl.get('newInvoice.invoiceCurrency') }}
              onChange={(e) => handleInputChange('idCurrency', e.target.value)}
              onBlur={(e) => handleInputChange('idCurrency', e.target.value)}
              error={!!errors?.idCurrency}
            >
              <MenuItem disabled value="">
                <em>{intl.get('newInvoice.invoiceCurrency')}</em>
              </MenuItem>
              {currencies?.map((currency) => (
                <MenuItem key={currency.id} value={currency.id}>
                  {currency.code}
                </MenuItem>
              ))}
            </Select>
            </Grid>
            {workflowState && !workflowLoading && !loading && (
              <Grid sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', maxWidth: '150px', marginTop: '15px' }}>
                <WorkflowStatusChip stateId={input.idWorkflowState} />
              </Grid>
            )}
        </Grid>

        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.addressFrom ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.invoiceAddressFrom')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              id="addressFrom"
              name="addressFrom"
              placeholder={intl.get('newInvoice.invoiceAddressFrom')}
              value={input?.addressFrom}
              onChange={(e) => handleInputChange('addressFrom', e.target.value)}
              onBlur={(e) => handleInputChange('addressFrom', e.target.value)}
              disabled={disabled}
              error={!!errors?.addressFrom}
            />
          </Grid>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.date ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.date')}
            </Typography>
            <DemoContainer components={['DatePicker']}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  slotProps={{
                    openPickerIcon: { fontSize: 'small' },
                    textField: {
                      size: 'small',
                      error: !!errors?.date,
                    },
                  }}
                  disabled={disabled}
                  value={new Date(input?.date)}
                  onChange={(e) => handleInputChange('date', e)}
                  sx={{
                    '& .MuiInputBase-root': {
                      overflow: 'hidden',
                      '&.Mui-error': {
                        border: '1px solid red !important',
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </DemoContainer>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.idTo ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.to')}
            </Typography>
            <Select
              fullWidth
              size="small"
              id="billTo"
              name="billTo"
              value={input?.idTo}
              displayEmpty
              disabled={disabled}
              inputProps={{ placeholder: intl.get('newInvoice.to') }}
              onChange={(e) => handleInputChange('idTo', e.target.value)}
              onBlur={(e) => handleInputChange('idTo', e.target.value)}
              error={!!errors?.idTo}
            >
              <MenuItem disabled value="">
                <em>{intl.get('newInvoice.to')}</em>
              </MenuItem>
              {billToPersonas?.map((persona) => (
                <MenuItem key={persona.id} value={persona.id}>
                  {persona.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.addressTo ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.invoiceAddressTo')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              id="addressTo"
              name="addressTo"
              placeholder={intl.get('newInvoice.invoiceAddressTo')}
              value={input?.addressTo}
              onChange={(e) => handleInputChange('addressTo', e.target.value)}
              onBlur={(e) => handleInputChange('addressTo', e.target.value)}
              disabled={disabled}
              error={!!errors?.addressTo}
            />
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ my: 3 }} />
      <Grid
        container
        spacing={2}
        size={{
          xl: 12,
          md: 6,
        }}
      >
        <Grid
          container
          className="justify-content-start"
          size={{
            xl: 6,
          }}
        >
          <Typography
            className={`${errors?.idInvoiceType ? 'text-danger' : ''}`}
            style={{ marginTop: '5px' }}
          >
            {intl.get('newInvoice.invoiceType')}{' '}
            {errors?.idInvoiceType && errors?.idInvoiceType !== 'missing confidence' && (
              <span className="text-danger">{errors?.idInvoiceType}</span>
            )}
          </Typography>
          <Grid
            size={{
              xl: 12,
            }}
          >
            <Select
              id="invoiceType"
              name="invoiceType"
              value={input?.idInvoiceType === 0 ? '' : input?.idInvoiceType}
              fullWidth
              displayEmpty
              disabled={disabled}
              inputProps={{
                placeholder: intl.get('newInvoice.invoiceType'),
              }}
              onChange={(e) => handleInputChange('idInvoiceType', e.target.value)}
              onBlur={(e) => handleInputChange('idInvoiceType', e.target.value)}
              error={errors?.idInvoiceType ? true : false}
            >
              <MenuItem disabled value="">
                <em>{intl.get('newInvoice.invoiceType')}</em>
              </MenuItem>
              {invoiceTypes?.map((invoiceType) => (
                <MenuItem key={invoiceType.id} value={invoiceType.id}>
                  {invoiceType.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Grid
          container
          className="justify-content-end"
          size={{
            xl: 6,
          }}
        >
          <Grid
            container
            className="justify-content-start"
            size={{
              xl: 10,
            }}
          >
            <Typography
              className={`${errors?.paymentTerms ? 'text-danger' : ''}`}
              style={{ marginTop: '5px' }}
            >
              {intl.get('newInvoice.paymentTerms')}{' '}
              {errors?.paymentTerms && errors?.paymentTerms !== 'missing confidence' && (
                <span className="text-danger">{errors?.paymentTerms}</span>
              )}
            </Typography>
          </Grid>
          <Grid
            container
            className="justify-content-end"
            size={{
              xl: 10,
            }}
          >
            <TextField
              id="paymentTerms"
              name="paymentTerms"
              placeholder={intl.get('newInvoice.paymentTerms')}
              value={input?.paymentTerms}
              onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
              onBlur={(e) => handleInputChange('paymentTerms', e.target.value)}
              disabled={disabled}
              fullWidth
              error={errors?.paymentTerms ? true : false}
            />
          </Grid>
        </Grid>
      </Grid>
      {errors?.invoiceItems && (
        <Grid
          container
          className="justify-content-start"
          style={{ marginTop: '10px', marginBottom: '-10px' }}
          size={{
            xl: 12,
          }}
        >
          <Typography className="text-danger">{errors?.invoiceItems}</Typography>
        </Grid>
      )}
      <Grid sx={{ marginTop: '20px', width: '100%', xl: 12 }}>
        <NewInvoiceItemTable disabled={disabled} />
      </Grid>
      <Grid
        container
        style={{ marginTop: '10px' }}
        size={{
          xl: 12,
        }}
      >
        <Grid
          size={{
            xl: 6,
          }}
        ></Grid>
        <Grid sx={{ marginTop: '20px', width: '100%' }} size={{ xl: 6 }}>
          <NewInvoiceSummary disabled={disabled} />
        </Grid>
      </Grid>
      <Grid className="justify-content-end">
        <Grid className="d-flex justify-content-end" sx={{ marginTop: '20px' }}>
          <WorkflowActionsBar
            viewMode=""
            onActionClick={handleSave}
            onSave={handleSaveOnly}
            onSaveBeforeAction={handleSaveBeforeAction}
            onBeforeAction={handleBeforeAction}
            returnPath="/invoicing"
            id={input.id}
            stateId={input.idWorkflowState}
            loading={loading}
            disabled={isDisabled()}
          />
          <InvoiceReconciliationDialog
            open={reconciliationOpen}
            invoiceId={input.id}
            invoiceAmount={input.amount ?? 0}
            currency={input.currency?.code ?? ''}
            onConfirm={handleReconciliationConfirm}
            onCancel={handleReconciliationCancel}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default InvoicingForm;
